import { BaseResponse } from "../../../shared/types";

export type BenefitServiceStatus =
  | "pendingReview"
  | "active"
  | "needsRevision"
  | "inactive"
  | "ownerNotResident";

export type BenefitDiscountType = "none" | "percent" | "fixedAmount" | "free";

export type BenefitContactType =
  | "telegram"
  | "phone"
  | "email"
  | "website"
  | "whatsapp"
  | "instagram";

export interface BenefitContact {
  type: BenefitContactType;
  value: string;
}

export interface BenefitServiceOwner {
  userId: string;
  firstName: string;
  lastName: string;
  caliber: string;
  avatarUrl?: string;
}

export interface BenefitServiceListItem {
  id: number;
  title: string;
  description: string;
  categoryId: number;
  categoryName: string;
  status: BenefitServiceStatus;
  isPartner: boolean;
  partnerName?: string;
  owner?: BenefitServiceOwner;
  photoUrl?: string;
  discountType: BenefitDiscountType;
  discountValue: number;
  promoCode?: string;
  promoStartsAt?: string;
  promoExpiresAt?: string;
  expiresAt?: string;
  publishedAt?: string;
  createdAt: string;
  updatedAt: string;
  isPromoLive: boolean;
}

export interface BenefitService extends BenefitServiceListItem {
  contacts: BenefitContact[];
  cityIds: number[];
  moderationComments?: string[];
  violatingFields?: string[];
}

export interface BenefitServicesResponse extends BaseResponse {
  pageToken?: string;
  services: BenefitServiceListItem[];
}

export interface BenefitServiceResponse extends BaseResponse {
  service: BenefitService;
}

export interface GetBenefitServicesParams {
  status?: BenefitServiceStatus;
  partner?: boolean;
  categoryId?: number;
  discountType?: BenefitDiscountType;
  hasPromoCode?: boolean;
  search?: string;
  sort?:
    | "created"
    | "updated"
    | "expires"
    | "published"
    | "promostarts"
    | "promoexpires"
    | "title"
    | "partner"
    | "discount";
  order?: "asc" | "desc";
  pageToken?: string;
  limit?: number;
}

export interface PartnerBenefitServiceBody {
  partnerName: string;
  title: string;
  description: string;
  categoryId: number;
  discountType: BenefitDiscountType;
  discountValue?: number;
  promoCode?: string;
  promoStartsAt?: string;
  promoExpiresAt?: string;
  expiresAt?: string;
  photoFileId?: string;
  contacts: BenefitContact[];
  cityIds: number[];
}

export interface RejectBenefitServiceBody {
  moderationComments: string[];
  violatingFields?: string[];
}

export interface BenefitPhotoUploadResponse extends BaseResponse {
  fileId: string;
  url: string;
}

export interface BenefitCategory {
  id: number;
  name: string;
}

export interface BenefitCategoriesResponse extends BaseResponse {
  categories: BenefitCategory[];
}

export const BENEFIT_SERVICE_STATUS_LABELS: Record<BenefitServiceStatus, string> =
  {
    pendingReview: "На модерации",
    active: "Активна",
    needsRevision: "На доработке",
    inactive: "Неактивна",
    ownerNotResident: "Владелец не резидент",
  };

export const BENEFIT_DISCOUNT_TYPE_LABELS: Record<BenefitDiscountType, string> = {
  none: "Без скидки",
  percent: "Процент",
  fixedAmount: "Фиксированная сумма",
  free: "Бесплатно",
};

export const BENEFIT_CONTACT_TYPE_LABELS: Record<BenefitContactType, string> = {
  telegram: "Telegram",
  phone: "Телефон",
  email: "Email",
  website: "Сайт",
  whatsapp: "WhatsApp",
  instagram: "Instagram",
};

/** Типы контактов, доступные для выбора в форме партнёрской услуги */
export const BENEFIT_CONTACT_TYPE_OPTIONS: BenefitContactType[] = [
  "telegram",
  "email",
  "website",
  "whatsapp",
];

/** Ключи полей для violatingFields (согласованы с мобильной формой) */
export const BENEFIT_VIOLATING_FIELD_OPTIONS = [
  { key: "title", label: "Название" },
  { key: "description", label: "Описание" },
  { key: "photo", label: "Фото" },
  { key: "category", label: "Категория" },
  { key: "discount", label: "Скидка" },
  { key: "promoCode", label: "Промокод" },
  { key: "contacts", label: "Контакты" },
  { key: "cities", label: "Города" },
] as const;
